import * as Yup from "yup";
import moment from "moment";
import { QUESTION_TYPES } from "components/Pages/CreateRFQ/ReturnableSchedulesCard/QuestionTypeComponent";
import {
	filterFieldValues,
	getFileNameFromUrl,
	getOriginalFileName,
} from "utils/helpers";
import { defaultOptions as defaultQuoteItems } from "components/Pages/CreateRFQ/QuoteCard/tableDefaultOptions";

export const getTabLists = (isTemplate) => {
	const baseTabs = [
		{
			value: 1,
			label: isTemplate ? "Template Details" : "RFx Details",
			description: isTemplate
				? "Provide template details"
				: "Provide project and RFx details",
			title: isTemplate ? "TEMPLATE DETAILS" : "RFx DETAILS",
			subTitle: isTemplate
				? "Please specify the details of the template."
				: "Please specify the details of the RFx and the required scope.",
		},
		{
			value: 2,
			label: "Documentation",
			description: "Upload relevant documents",
			title: "Documentation",
			subTitle:
				"Identify and include relevant project documents such as Project Briefs, Plans and Drawings, Approvals, Terms and Conditions, etc.",
		},
		{
			value: 3,
			label: "Returnable Schedules",
			description: "Create your questionnaire",
			title: "Returnable Schedules",
			subTitle:
				"Here you can create requests or questions and customise how answers can be provided by using the 'CUSTOMISE' dropdown. If you have returnable schedules for the respondents you can upload these as well.",
		},
		{
			value: 4,
			label: "Quote",
			description: "Create/Upload a quoting template",
			title: "QUOTE",
			subTitle:
				"Create or upload a quoting template for the Supplier to complete as part of their response. \nSimply describe the scope and nominate a unit of measurement from the dropdown.",
		},
	];

	if (!isTemplate) {
		baseTabs.push({
			value: 5,
			label: "RFx Participants",
			description: "Confirm RFx participants and send",
			title: "RFx Participants",
			subTitle:
				"Select suppliers that will receive the RFQ. You can also invite guest users.",
		});
	}

	return baseTabs;
};

const defaultSections = [
	{
		id: null,
		name: "",
		description: "",
		sort_order: 1,
		isRemove: 0,
		questionnaire: [
			{
				id: null,
				question: "",
				rfq_question_type_id: 2,
				required: 1,
				fields: [""],
				name: "",
				sort_order: 1,
			},
		],
	},
];

const defaultQuoteStructure = [
	{
		name: "",
		description: "",
		items: defaultQuoteItems(),
		sort_order: 1,
		required: 1,
		isRemove: 0,
	},
];

const defaultDeadlineTime = "16:00";

// formValuesChecker: Determines initial/default values based on template flag
export const formValuesChecker = (rfqData, isTemplate) => {
	// --- Handle NEW RFX / Template Creation ---
	if (!rfqData) {
		const baseInitialValues = {
			documentation_description: "",
			documents: [],
			returnable_schedule_type: "form",
			sections: defaultSections,
			returnable_schedule_file: "",
			returnable_schedule_file_path: "",
			returnable_schedule_file_id: null,
			quote: defaultQuoteStructure,
			quote_type: "form",
			quote_file: "",
			quote_file_path: "",
			allow_flexible_quote: "0",
			deadline_time: defaultDeadlineTime,
		};

		if (isTemplate) {
			return {
				template_name: "",
				...baseInitialValues,
			};
		} else {
			// Initial values for a new RFX
			return {
				name: "",
				rfq_id_number: "",
				type: "",
				address: {
					latitude: "",
					longitude: "",
					value: "",
				},
				rfx_lead_id: "",
				description: "",
				client: "",
				deadline_date: "",
				deadline_time: defaultDeadlineTime,
				submission_deadline_timezone: "AWST",
				escope_of_service: "",
				suppliers: [],
				guest: [],
				...baseInitialValues,
			};
		}
	}

	// --- Handle EXISTING RFX / Template Data ---
	const effectiveData = rfqData.data || rfqData;

	const documents = effectiveData?.documentationDocument?.map((doc) => ({
		id: doc.id || null,
		document: doc.fileName || null,
		category_id: doc.categoryId || null,
		sort_order: doc.sortOrder || null,
		file_size: doc?.fileSize || null,
		path: doc.documentPath || null,
		isRemove: 0,
	}));

	const sections = effectiveData?.questionnnaire?.map((section, i) => ({
		id: section.id || null,
		name: section.name || "",
		description: section.description || "",
		sort_order: section.sortOrder ? parseInt(section.sortOrder) : i + 1,
		isRemove: 0,
		questionnaire: section.question?.map((question, j) => {
			let fields;
			if (question.type === QUESTION_TYPES.CORPORATE_DOCUMENTS) {
				fields = question.fields;
			} else {
				fields = filterFieldValues(question) || [];
			}

			return {
				id: question.id || null,
				name: question.name || "",
				fields: fields,
				sort_order: j + 1,
				required: question.required ? 1 : 0,
				rfq_question_type_id: question.type ? parseInt(question.type) : null,
				isRemove: 0,
			};
		}),
	}));

	const quotes = effectiveData?.quotes?.map((quote) => ({
		id: quote.id || null,
		name: quote.name || "",
		description: quote.description || "",
		sort_order: quote.sortOrder || null,
		required: 1,
		isRemove: 0,
		items: quote?.items.map((item, k) => ({
			id: item.id || null,
			name: item.name || "",
			unit_id: item.unit?.id ? item.unit?.id : item?.unit_id,
			sort_order: k + 1,
			placeholder: item.placeholder || "",
			isRemove: item?.isRemove || 0,
		})),
	}));

	const suppliers = effectiveData?.supplierInvited?.all
		?.filter((supplier) => supplier.company)
		.map((supplier) => supplier?.company.id);
	const guest = effectiveData?.supplierInvited?.all
		?.filter((supplier) => !supplier.company)
		.map((supplier) => ({ name: supplier.name, email: supplier.email }));

	// Determine the actual quote file path URL string
	let actualQuoteUrl = "";
	if (typeof effectiveData?.quoteFilePath === "string") {
		actualQuoteUrl = effectiveData.quoteFilePath;
	} else if (
		typeof effectiveData?.quoteFilePath === "object" &&
		effectiveData?.quoteFilePath?.file
	) {
		// Handle cases - might be an object like { id: ..., file: 'url' }
		actualQuoteUrl = effectiveData.quoteFilePath.file;
	}

	const quoteType = effectiveData?.quoteType || "form";
	const processedQuotes = quotes || [];

	const finalQuotes =
		quoteType === "form" && processedQuotes.length === 0
			? defaultQuoteStructure
			: processedQuotes;

	const finalSections =
		sections && sections.length > 0 ? sections : defaultSections;

	const returnableScheduleType =
		effectiveData?.returnableScheduleType || "form";
	const returnableScheduleFileInfo = effectiveData?.returnableScheduleFilePath;

	let returnableScheduleFileId = null;
	let returnableScheduleFileName = "";
	let returnableScheduleFilePath = "";

	if (returnableScheduleType === "file") {
		if (isTemplate) {
			returnableScheduleFileId = returnableScheduleFileInfo?.id || null;
			returnableScheduleFileName = returnableScheduleFileInfo?.file_name || "";
			returnableScheduleFilePath = "";
		} else {
			returnableScheduleFileId = null;
			returnableScheduleFileName = returnableScheduleFileInfo || "";
			returnableScheduleFilePath = returnableScheduleFileInfo?.file_path || "";
		}
	}

	// Extract original filename from quote file path for display
	let displayQuoteFileName = "";
	let quoteFileSize = null;
	if (actualQuoteUrl && quoteType === "file") {
		const serverFileName = getFileNameFromUrl(actualQuoteUrl);
		displayQuoteFileName = getOriginalFileName(serverFileName);
		// Get file size from server response
		quoteFileSize = effectiveData?.quoteFilePath?.file_size || null;
	}

	// Common values for both templates and RFQs
	const commonValues = {
		documentation_description: effectiveData?.documentationDescription || "",
		documents: documents || [],
		returnable_schedule_type: returnableScheduleType,
		sections: finalSections,
		returnable_schedule_file_id: returnableScheduleFileId,
		returnable_schedule_file: returnableScheduleFileName,
		returnable_schedule_file_path: returnableScheduleFilePath,
		quote: finalQuotes,
		quote_type: quoteType,
		quote_file: displayQuoteFileName || "",
		quote_file_path: actualQuoteUrl,
		quote_file_size: quoteFileSize,
		allow_flexible_quote: effectiveData?.allow_flexible_quote ? "1" : "0",
	};

	if (isTemplate) {
		let templateQuoteFileId = null;
		let templateQuoteFileName = commonValues.quote_file;
		let templateQuoteFilePath = "";

		if (commonValues.quote_type === "file") {
			templateQuoteFileId = effectiveData?.quoteFilePath?.id || null;

			if (effectiveData?.quoteFilePath?.file_name) {
				templateQuoteFileName = getOriginalFileName(
					effectiveData.quoteFilePath.file_name
				);
			}
		}

		return {
			template_name: rfqData?.name || effectiveData?.name || "",
			...commonValues,
			quote_file_id: templateQuoteFileId,
			quote_file: templateQuoteFileName,
			quote_file_path: templateQuoteFilePath,
			returnable_schedule_file_path: "",
		};
	} else {
		return {
			name: effectiveData?.name || "",
			rfq_id_number: effectiveData?.rfqIdNumber || "",
			type: effectiveData?.type || "",
			address: {
				latitude: effectiveData?.locationLatitude || "",
				longitude: effectiveData?.locationLongitude || "",
				value: effectiveData?.location || "",
			},
			rfx_lead_id: effectiveData?.rfxLead?.id || "",
			description: effectiveData?.description || rfqData?.project?.description,
			client: effectiveData?.client || "",
			deadline_date: effectiveData?.submissionDeadline
				? moment(effectiveData?.submissionDeadline)
				: "",
			deadline_time: effectiveData?.submissionDeadline
				? moment(effectiveData?.submissionDeadline).format("HH:mm")
				: defaultDeadlineTime,
			submission_deadline_timezone: effectiveData?.submissionTimezone || "AWST",
			escope_of_service: effectiveData?.escopeOfService || "",
			suppliers: suppliers || [],
			guest: guest || [],
			...commonValues,
			quote_file_id: null, // Explicitly clear quote ID for RFX
			returnable_schedule_file_id: null, // Keep this override for returnable schedules
		};
	}
};

const generateQuestionSchema = () => {
	const typesChecker = (type) => {
		switch (type) {
			case QUESTION_TYPES.SINGLE_OPTION:
				return "Option";
			case QUESTION_TYPES.MULTIPLE_CHOICE:
				return "Checkbox";
			case QUESTION_TYPES.DROP_DOWN:
				return "Selection";
			default:
				return "Option";
		}
	};
	return Yup.object().shape({
		id: Yup.string().nullable(),
		name: Yup.string().when("isRemove", {
			is: (val) => val === 0 || val === null || val === undefined,
			then: Yup.string().required("Question name is required"),
			otherwise: Yup.string().nullable(),
		}),
		fields: Yup.mixed().when(["rfq_question_type_id", "isRemove"], {
			is: (rfq_question_type_id, isRemove) =>
				(isRemove === 0 || isRemove === null || isRemove === undefined) &&
				parseInt(rfq_question_type_id) === QUESTION_TYPES.CORPORATE_DOCUMENTS,
			then: Yup.object().shape({
				type: Yup.string().required(),
				allow_document_categories: Yup.array()
					.required("Document categories are required")
					.min(1, "At least one document category is required")
					.of(
						Yup.object().shape({
							category_id: Yup.mixed().required(
								"Document category is required"
							),
							subcategory_id: Yup.mixed().nullable(),
						})
					),
			}),
			otherwise: Yup.mixed().when(
				"rfq_question_type_id",
				(rfq_question_type_id, schema) => {
					// Handle table validation
					if (parseInt(rfq_question_type_id) === QUESTION_TYPES.TABLE) {
						let index = 0;
						// Try to use schema if it exists, otherwise create new one
						const tableSchema =
							schema && typeof schema.of === "function" ? schema : Yup.array();

						return tableSchema.of(
							Yup.object().shape({
								header: Yup.string().required("Header value is required"),
								values: Yup.array()
									.when("$", (_, schema) => {
										if (index === 0) {
											index++;
											return Yup.array().of(
												Yup.mixed().required("Question is required")
											);
										}
										return Yup.array().of(Yup.string().nullable());
									})
									.required("Values array is required"),
							})
						);
					}

					// Handle multiple choice, single option, dropdown validation
					const isOptionType = [
						QUESTION_TYPES.SINGLE_OPTION,
						QUESTION_TYPES.MULTIPLE_CHOICE,
						QUESTION_TYPES.DROP_DOWN,
					].includes(parseInt(rfq_question_type_id));

					if (isOptionType) {
						// Try to use schema if it exists, otherwise create new one
						const optionSchema =
							schema && typeof schema.of === "function" ? schema : Yup.array();

						return optionSchema.of(
							Yup.string().required(
								`${typesChecker(parseInt(rfq_question_type_id))} name is required`
							)
						);
					}

					return Yup.mixed().nullable();
				}
			),
		}),
		sort_order: Yup.number().nullable(),
		required: Yup.number().oneOf([0, 1]).required(),
		rfq_question_type_id: Yup.string().required(
			"Please select the question type"
		),
	});
};

const generateSectionSchema = () => {
	return Yup.object().shape({
		id: Yup.string().nullable(),
		name: Yup.string().when("isRemove", {
			is: (val) => val === 0 || val === null || val === undefined,
			then: Yup.string().required("Section name is required"),
			otherwise: Yup.string().nullable(),
		}),
		description: Yup.string().when("isRemove", {
			is: (val) => val === 0 || val === null || val === undefined,
			then: Yup.string().required("Description is required"),
			otherwise: Yup.string().nullable(),
		}),
		sort_order: Yup.number().nullable(),
		questionnaire: Yup.array().when("isRemove", {
			is: (val) => val === 0 || val === null || val === undefined,
			then: Yup.array()
				.of(generateQuestionSchema())
				.min(1, "Please add at least 1 question for this section"),
			otherwise: Yup.array().nullable(),
		}),
	});
};

const generateGuestSchema = () => {
	return Yup.object().shape({
		name: Yup.string().required("Name is required"),
		email: Yup.string().email("Invalid email").required("Email is required"),
	});
};

const generateQuoteItemSchema = () => {
	return Yup.object().shape({
		id: Yup.string().nullable(),
		name: Yup.string().required("Item name is required"),
		unit_id: Yup.string().required("Unit of Measurement is not selected"),
		sort_order: Yup.number().nullable(),
	});
};

const generateQuoteSchema = () => {
	return Yup.object().shape({
		id: Yup.string().nullable(),
		name: Yup.string().required("Section name is required"),
		description: Yup.string().required("Section description is required"),
		sort_order: Yup.number().nullable(),
		required: Yup.number().oneOf([1]).required(),
		isRemove: Yup.number().oneOf([0, 1]).required(),
		items: Yup.array()
			.test("rowTotal", "At least one row is required", function (value) {
				return value.filter((item) => !item.isRemove).length > 0;
			})
			.of(generateQuoteItemSchema()),
	});
};

export const validationSchema = (isTemplate) => {
	// Define validation rules for each step
	const stepValidations = {
		// Step 1: RFx Details
		details: {
			// For templates, we only need to validate the template_name
			// For RFQs, we need to validate all fields
			...(isTemplate
				? {
						template_name: Yup.string().required("Template name is required"),
					}
				: {
						name: Yup.string().required("Request Title is required"),
						type: Yup.string().required("Type is required"),
						client: Yup.string().required("Client is required"),
						rfx_lead_id: Yup.number().required("RFx lead is required"),
						deadline_date: Yup.date().required("Deadline date is required"),
						deadline_time: Yup.string().required("Time is required"),
						address: Yup.object().shape({
							value: Yup.string().required("Location is required"),
						}),
					}),
		},

		// Step 2: Documentation - can be empty for both templates and RFQs
		documentation: {
			documents: Yup.array().nullable(),
			documentation_description: Yup.string().test(
				"len",
				"Input has exceeded the max character limit",
				(val) => (val ? val?.replace(/<\/?[^>]+>/gi, "").length <= 1500 : true)
			),
		},

		// Step 3: Returnable Schedules - Same validation for both templates and RFQs
		schedules: {
			sections: Yup.array().when("returnable_schedule_type", (type, schema) =>
				type !== "file"
					? schema.of(generateSectionSchema()).min(1, "Section is required")
					: schema.nullable()
			),
			returnable_schedule_file: Yup.mixed().when("returnable_schedule_type", {
				is: "file",
				then: Yup.mixed().required(),
			}),
		},

		// Step 4: Quote - Same validation for both templates and RFQs
		quote: {
			quote: Yup.array().when("quote_type", (type, schema) =>
				type !== "file"
					? schema.of(generateQuoteSchema()).min(1, "Quote is required")
					: schema.nullable()
			),
			quote_file: Yup.mixed().when("quote_type", {
				is: "file",
				then: Yup.mixed().required("Template file is required"),
			}),
			allow_flexible_quote: Yup.string().oneOf(["0", "1"]).required(),
		},

		// Step 5: RFx Participants - Only for RFQs, not for templates
		participants: {
			guest: Yup.array().of(generateGuestSchema()),
		},
	};

	// Combine all validation rules into one schema
	return Yup.object().shape({
		...stepValidations.details,
		...stepValidations.documentation,
		...stepValidations.schedules,
		...stepValidations.quote,
		...(isTemplate ? {} : stepValidations.participants),
	});
};

export const validStepChecker = (step, errors, isTemplate) => {
	if (errors) {
		if (step === 1) {
			return {
				isValid: isTemplate
					? !errors.template_name
					: !errors.type &&
						!errors.client &&
						!errors.deadline_date &&
						!errors.name,
				errorMessage: isTemplate
					? "Please enter a template name"
					: "Please fill all required fields",
			};
		}
		if (step === 2) {
			return {
				isValid: !errors.documents,
				errorMessage: "Please upload at least one document",
			};
		}
		if (step === 3) {
			// Check for section errors
			const sectionErrors = Object.keys(errors).some(
				(key) =>
					key.includes("sections") || key.includes("returnable_schedule_file")
			);

			return {
				isValid: !sectionErrors,
				errorMessage: "Please fill all required fields",
			};
		}
		if (step === 4) {
			const quoteErrors = Object.keys(errors).some((key) =>
				key.includes("quote")
			);
			return {
				isValid: !quoteErrors,
				errorMessage: "Please fill all quote fields",
			};
		}
		if (step === 5 && !isTemplate) {
			return {
				isValid: !errors.guest,
				errorMessage: "Please fill all required fields",
			};
		}
	}
	return { isValid: true, errorMessage: "" };
};

export const validateReturnableSchedulesStep = (currentFormValues) => {
	if (currentFormValues?.returnable_schedule_type === "form") {
		if (currentFormValues?.sections?.length > 0) {
			if (
				currentFormValues?.sections?.length === 1 &&
				currentFormValues?.sections[0]?.name === "deleted"
			) {
				return {
					message: "At least one section with questionnaire is required",
					title: "Section Required",
				};
			} else {
				const nullQuestionnaire = isReturnableQuestionnaireNull(
					currentFormValues?.sections
				);
				const nullSection = currentFormValues?.sections?.find(
					(currentSection) => currentSection.name === ""
				);

				if (nullSection)
					return {
						message: "Please enter the value of section",
						title: "Section value is required",
						data: nullSection,
					};

				if (nullQuestionnaire)
					return {
						message: "Please enter the value of questionnaire",
						title: "Question value is required",
						data: nullQuestionnaire,
					};
			}
		}
	} else {
		if (
			!currentFormValues?.returnable_schedule_type ||
			currentFormValues?.returnable_schedule_type?.length < 1
		) {
			return {
				message: "Please upload returnables schedules templates",
				title: "Returnable schedules template is required",
				data: "returnable_schedule_type",
			};
		}
	}
};

const isReturnableQuestionnaireNull = (currentFormSection) => {
	return currentFormSection?.find((currentSection) => {
		return currentSection?.questionnaire?.find(
			(question) => question?.name === ""
		);
	});
};

export const isQuoteFormInvalid = (quoteArray) => {
	if (!quoteArray || !Array.isArray(quoteArray)) {
		return true;
	}
	// Check each non-removed quote section
	for (const section of quoteArray) {
		if (!section.isRemove) {
			if (!section.name || !section.description) {
				return true;
			}
			// Check each non-removed item within the section
			if (!section.items || !Array.isArray(section.items)) return true;
			for (const item of section.items) {
				if (!item.isRemove) {
					// Check required item fields
					if (!item.name || !item.unit_id) {
						return true;
					}
				}
			}
		}
	}
	return false;
};
